import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import MarketingShell from "./MarketingShell";
import { StatusChip } from "./MarketingOverview";
import EmailBlockBuilder, {
  emptyBlocks, extractBlocks, renderBlocksToHtml, wrapBlocksInHtml, blocksFromRawHtml,
} from "./EmailBlockBuilder";
import { PaperPlaneTilt, FloppyDisk, Eye, Envelope, DeviceMobile, Desktop, CalendarBlank, PaperPlaneRight, ShieldCheck, Clock, X, Check, Buildings, UserCircle, Upload, Stack as StackIcon } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";
import { SafeHtml, safeSrcDoc } from "@/lib/safeHtml";

/* ───────── helpers ───────── */
const SOURCE_COLORS = {
  csv_import: { bg: "#EFF6FF", fg: "#0055FF", label: "CSV" },
  client: { bg: "#F0FDF4", fg: "#16A34A", label: "Client" },
  crm_lead: { bg: "#F5F3FF", fg: "#7C3AED", label: "Lead" },
};

function SourceChip({ source }) {
  const c = SOURCE_COLORS[source] || { bg: "#F5F5F5", fg: "#525252", label: source };
  return <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: c.bg, color: c.fg }}>{c.label}</span>;
}

/* ───────── main page ───────── */
export default function CampaignEditorPage() {
  const { campaignId: paramId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "platform_admin";
  const [campaignId, setCampaignId] = useState(paramId);
  const [loading, setLoading] = useState(true);
  const [camp, setCamp] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [section, setSection] = useState("details");

  // Audience picker state
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);

  // Preview state
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [previewContactId, setPreviewContactId] = useState("");
  const [previewContacts, setPreviewContacts] = useState([]);
  const [previewRendered, setPreviewRendered] = useState(null);

  // Spam linter
  const [lint, setLint] = useState(null);
  useEffect(() => {
    if (section === "review" && campaignId) {
      api.get(`/marketing/campaigns/${campaignId}/lint`).then(r => setLint(r.data)).catch(() => {});
    }
  }, [section, campaignId]);

  // Schedule modal
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  // Default test-send recipient is `sales@eyvo.com` per Mike's request
  // (was previously seeded with the logged-in user's email — which made
  // most operators send the test back to themselves and forget the sales
  // team's deliverability QA loop). Operators can still type any email.
  const [testEmails, setTestEmails] = useState("sales@eyvo.com");
  // Preview popover wired to the top-bar "Preview" button — same backend
  // endpoint as the Review-tab Test send, just one click away from any
  // step of the editor.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("sales@eyvo.com");
  const [previewSending, setPreviewSending] = useState(false);

  // ── bootstrap ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let id = paramId;
        if (!id) {
          const r = await api.post("/marketing/campaigns", { name: "Untitled Campaign" });
          id = r.data.id;
          nav(`/marketing/campaigns/${id}/edit`, { replace: true });
        }
        setCampaignId(id);
        const [cR, lR, sR, cntR] = await Promise.all([
          api.get(`/marketing/campaigns/${id}`),
          api.get("/marketing/lists"),
          api.get("/marketing/segments"),
          api.get("/marketing/contacts?limit=20"),
        ]);
        // Apply any pending draft dropped by the AI assistant
        let loadedCamp = cR.data;
        const pending = sessionStorage.getItem("pulse_ai_pending_campaign_draft");
        if (pending) {
          let draftApplied = false;
          let hasBody = false;
          try {
            const draft = JSON.parse(pending);
            const patch = {};
            if (draft.name) patch.name = draft.name;
            if (draft.subject_line || draft.subject) patch.subject = draft.subject_line || draft.subject;
            if (draft.preview_text) patch.preview_text = draft.preview_text;
            if (draft.from_name) patch.from_name = draft.from_name;
            if (Array.isArray(draft.body_blocks) && draft.body_blocks.length > 0) {
              const blocks = draft.body_blocks.map((b, i) => ({
                id: `ai-${i}-${Math.random().toString(36).slice(2, 6)}`, ...b,
              }));
              const visible = renderBlocksToHtml(blocks);
              patch.body_html = wrapBlocksInHtml(blocks, visible);
              hasBody = true;
            } else if (typeof draft.body_html === "string" && draft.body_html.trim()) {
              patch.body_html = draft.body_html;
              hasBody = true;
            }
            if (Object.keys(patch).length > 0) {
              await api.patch(`/marketing/campaigns/${id}`, patch);
              loadedCamp = { ...loadedCamp, ...patch };
              draftApplied = true;
            }
          } catch {
            /* ignore bad drafts */
          }
          sessionStorage.removeItem("pulse_ai_pending_campaign_draft");
          if (draftApplied && hasBody) {
            toast.success("AI draft applied — review and tweak below.");
          } else if (draftApplied && !hasBody) {
            toast.warning("AI draft only had headers — please add body copy in the Content step.");
          } else {
            toast.error("Pulse AI draft was empty. Please try a more specific prompt.");
          }
        }
        setCamp(loadedCamp);
        setLists(lR.data);
        setSegments(sR.data);
        setPreviewContacts(cntR.data.items || []);
      } catch (e) {
        toast.error("Failed to load campaign");
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramId]);

  const update = (patch) => {
    setCamp(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  // ── save ─────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!camp) return;
    const payload = {
      name: camp.name, internal_note: camp.internal_note, subject: camp.subject,
      preview_text: camp.preview_text, from_name: camp.from_name,
      from_email: camp.from_email, reply_to: camp.reply_to,
      body_html: camp.body_html, body_text: camp.body_text,
      audience_definition: camp.audience_definition,
    };
    try {
      await api.put(`/marketing/campaigns/${campaignId}`, payload);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  }, [camp, campaignId]);

  // ── audience preview ─────────────────────────────────────────────────────
  const refreshAudiencePreview = async () => {
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/audience/preview`, {
        audience_definition: camp?.audience_definition || {},
      });
      setAudiencePreview(r.data);
    } catch (e) {
      // A refused audience (e.g. client check unavailable) must not read
      // as "0 recipients".
      const d = e?.response?.data?.detail;
      setAudiencePreview({ error: typeof d === "string" ? d : "Could not calculate the recipients." });
    }
  };
  useEffect(() => {
    if (camp?.audience_definition && section === "audience") refreshAudiencePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camp?.audience_definition, section]);

  // ── rendered preview ─────────────────────────────────────────────────────
  const loadPreview = useCallback(async () => {
    try {
      const qs = previewContactId ? `?contact_id=${previewContactId}` : "";
      const r = await api.get(`/marketing/campaigns/${campaignId}/preview${qs}`);
      setPreviewRendered(r.data);
    } catch { setPreviewRendered(null); }
  }, [campaignId, previewContactId]);
  useEffect(() => {
    if (section === "content" || section === "review") loadPreview();
  }, [section, loadPreview]);

  // ── toggle audience items ────────────────────────────────────────────────
  const toggleItem = (key, id) => {
    const adef = camp.audience_definition || {};
    const arr = new Set(adef[key] || []);
    if (arr.has(id)) arr.delete(id); else arr.add(id);
    update({ audience_definition: { ...adef, [key]: [...arr] } });
  };
  const isChecked = (key, id) =>
    (camp?.audience_definition?.[key] || []).includes(id);
  const setIncludeClients = (on) =>
    update({ audience_definition: { ...(camp.audience_definition || {}), include_clients: on } });

  // ── actions ──────────────────────────────────────────────────────────────
  // Shared test-send routine — used by BOTH the Review-tab "Test send"
  // field AND the top-bar Preview popover. Accepts a raw recipient
  // string (comma/space/semicolon-separated) so the two callsites can
  // pass their own input box value.
  const doTestSendTo = async (recipientString) => {
    const recips = (recipientString || "").split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (recips.length === 0) {
      toast.error("Enter at least one email address.");
      return { ok: false };
    }
    if (dirty) await save();
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/test-send`, { recipients: recips });
      const failedCount = r.data.failed?.length || 0;
      if (r.data.sent > 0 && failedCount === 0) {
        toast.success(`Preview sent to ${recips.join(", ")}.`);
      } else if (r.data.sent > 0) {
        toast.warning(`Preview sent to ${r.data.sent} address${r.data.sent === 1 ? "" : "es"}; ${failedCount} failed.`);
      } else {
        toast.error("All test sends failed. Check the recipient(s) and try again.");
      }
      return { ok: r.data.sent > 0, data: r.data };
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed.");
      return { ok: false };
    }
  };
  const doTestSend = () => doTestSendTo(testEmails);
  const doPreviewSend = async () => {
    setPreviewSending(true);
    const res = await doTestSendTo(previewEmail);
    setPreviewSending(false);
    if (res.ok) setPreviewOpen(false);
  };

  const doSendNow = async () => {
    if (!window.confirm(`Send this campaign to ${audiencePreview?.total || 0} recipients right now?`)) return;
    if (dirty) await save();
    try {
      await api.post(`/marketing/campaigns/${campaignId}/send-now`);
      toast.success("Campaign queued — check the report for progress");
      nav(`/marketing/campaigns/${campaignId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const doSchedule = async () => {
    if (!scheduleAt) return toast.error("Pick a date/time");
    if (dirty) await save();
    try {
      await api.post(`/marketing/campaigns/${campaignId}/schedule`, {
        scheduled_at: new Date(scheduleAt).toISOString(),
      });
      toast.success("Campaign scheduled");
      setScheduleOpen(false);
      nav(`/marketing/campaigns/${campaignId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schedule failed");
    }
  };

  // ── Approval actions ───────────────────────────────────────────────────
  const requestApproval = async () => {
    if (dirty) await save();
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/request-approval`);
      setCamp({ ...camp, approval_status: r.data.approval_status });
      toast.success("Approval requested — a platform admin will review.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    }
  };
  const approveNow = async () => {
    const notes = window.prompt("Approval notes (optional):", "") || "";
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/approve`, { notes });
      setCamp({ ...camp, approval_status: r.data.approval_status,
                approved_by: r.data.approved_by, approved_at: r.data.approved_at });
      toast.success("Campaign approved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };
  const rejectNow = async () => {
    const notes = window.prompt("Reason for rejection (required):", "");
    if (!notes) return;
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/reject`, { notes });
      setCamp({ ...camp, approval_status: r.data.approval_status,
                approved_by: null, approved_at: null, approval_notes: notes });
      toast.success("Campaign rejected — sent back to draft");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  if (loading || !camp) {
    return <MarketingShell><p className="text-sm text-[#A3A3A3]">Loading editor…</p></MarketingShell>;
  }

  // A sent campaign is editable. The send already happened and
  // `mkt_sends` holds the personalisation snapshot each recipient
  // actually received, so editing the campaign record cannot rewrite
  // history — it lets an operator correct copy and re-use it.
  // `sending` stays locked: a send in flight would give the first
  // half of the audience one email and the second half another.
  const readOnly = ["sending", "archived"].includes(camp.status);

  return (
    <MarketingShell>
      {/* Top bar: name + status + save */}
      <div className="bg-white border border-[#E5E5E5] p-4 mb-4 flex items-center gap-3 flex-wrap">
        <input
          value={camp.name || ""}
          onChange={e => update({ name: e.target.value })}
          disabled={readOnly}
          className="text-lg font-black tracking-tight flex-1 bg-transparent focus:outline-none border-0 min-w-[200px]"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          data-testid="campaign-name"
        />
        <StatusChip status={camp.status} />
        {dirty && <span className="text-[10px] font-bold uppercase tracking-wider text-[#D97706]">Unsaved</span>}
        {!readOnly && (
          <button onClick={save} className="h-8 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] flex items-center gap-1" data-testid="save-btn">
            <FloppyDisk size={12} />Save
          </button>
        )}
        {!readOnly && (
          <div className="relative">
            <button onClick={() => setPreviewOpen(o => !o)}
                    className="h-8 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] flex items-center gap-1"
                    data-testid="preview-btn"
                    title="Send a one-off preview of this campaign to any address">
              <Eye size={12} />Preview
            </button>
            {previewOpen && (
              <>
                {/* Backdrop catches outside-clicks to close the popover */}
                <div className="fixed inset-0 z-40" onClick={() => setPreviewOpen(false)} data-testid="preview-popover-backdrop" />
                <div className="absolute right-0 top-9 z-50 w-[340px] bg-white border border-[#E5E5E5] shadow-lg p-4"
                     data-testid="preview-popover"
                     onClick={e => e.stopPropagation()}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252] mb-2">Send preview to</p>
                  <input type="email"
                         value={previewEmail}
                         onChange={e => setPreviewEmail(e.target.value)}
                         onKeyDown={e => { if (e.key === "Enter" && !previewSending) { e.preventDefault(); doPreviewSend(); } }}
                         className="w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                         placeholder="sales@eyvo.com"
                         data-testid="preview-email-input"
                         autoFocus />
                  <p className="text-[10px] text-[#A3A3A3] mt-1.5 leading-snug">
                    The email arrives with subject prefixed <code className="bg-[#F5F5F5] px-1">[TEST]</code> and is rendered against a sample contact. Tokens, footer and tracking pixel all behave like a live send.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setPreviewOpen(false)}
                            className="flex-1 h-8 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider"
                            data-testid="preview-cancel">Cancel</button>
                    <button onClick={doPreviewSend}
                            disabled={previewSending || !previewEmail.trim()}
                            className={`flex-1 h-8 text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                              previewSending || !previewEmail.trim()
                                ? "bg-[#A3A3A3] cursor-not-allowed"
                                : "bg-[#0055FF] hover:bg-[#0040CC]"
                            }`}
                            data-testid="preview-send-confirm">
                      <PaperPlaneTilt size={12} weight="fill" />
                      {previewSending ? "Sending…" : "Send preview"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {!readOnly && (
          <>
            <button onClick={() => setSection("review")} className="h-8 px-4 bg-[#0055FF] hover:bg-[#0040CC] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="review-btn">
              <PaperPlaneTilt size={12} weight="fill" />Review &amp; Send
            </button>
          </>
        )}
      </div>

      <ApprovalBar camp={camp} isAdmin={isAdmin}
                   onRequest={requestApproval}
                   onApprove={approveNow}
                   onReject={rejectNow} />

      {/* Step tabs */}
      <div className="flex gap-0.5 mb-4 bg-white border border-[#E5E5E5]">
        {[
          { k: "details", label: "1 · Details" },
          { k: "audience", label: "2 · Audience" },
          { k: "content", label: "3 · Content" },
          { k: "review", label: "4 · Review & Send" },
        ].map(s => (
          <button
            key={s.k}
            onClick={() => setSection(s.k)}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
              section === s.k ? "bg-[#0055FF] text-white" : "bg-white text-[#525252] hover:bg-[#FAFAFA]"
            }`}
            data-testid={`step-${s.k}`}
          >{s.label}</button>
        ))}
      </div>

      {/* Section bodies */}
      {section === "details" && <DetailsSection camp={camp} update={update} readOnly={readOnly} campaignId={campaignId} />}
      {section === "audience" && (
        <AudienceSection camp={camp} lists={lists} segments={segments}
                         toggleItem={toggleItem} isChecked={isChecked}
                         setIncludeClients={setIncludeClients}
                         preview={audiencePreview} />
      )}
      {section === "content" && (
        <ContentSection camp={camp} update={update} readOnly={readOnly}
                        previewDevice={previewDevice} setPreviewDevice={setPreviewDevice}
                        previewContactId={previewContactId}
                        setPreviewContactId={(v) => { setPreviewContactId(v); }}
                        previewContacts={previewContacts} previewRendered={previewRendered}
                        loadPreview={loadPreview} />
      )}
      {section === "review" && (
        <ReviewSection camp={camp} audiencePreview={audiencePreview}
                       previewRendered={previewRendered}
                       lint={lint}
                       testEmails={testEmails} setTestEmails={setTestEmails}
                       onTestSend={doTestSend}
                       onSendNow={doSendNow}
                       onSchedule={() => setScheduleOpen(true)}
                       onRequestApproval={requestApproval}
                       update={update} />
      )}

      {/* Schedule modal */}
      {scheduleOpen && (
        <ScheduleModal
          value={scheduleAt}
          onChange={setScheduleAt}
          onCancel={() => setScheduleOpen(false)}
          onConfirm={doSchedule}
        />
      )}
    </MarketingShell>
  );
}

/* ───────── Schedule modal with Best-Time suggestion ───────── */
function ScheduleModal({ value, onChange, onCancel, onConfirm }) {
  const [insights, setInsights] = useState(null);
  useEffect(() => {
    api.get("/marketing/reports/send-time-insights")
       .then(r => setInsights(r.data)).catch(() => {});
  }, []);

  const applyBest = () => {
    if (!insights?.top_slots?.length) return;
    const slot = insights.top_slots[0];
    // Build a local datetime for the next occurrence of `slot.dow` at `slot.hour:00 UTC`.
    const now = new Date();
    const currentDow = (now.getUTCDay() + 6) % 7; // Mon=0..Sun=6
    let delta = (slot.dow_idx - currentDow + 7) % 7;
    if (delta === 0 && now.getUTCHours() >= slot.hour) delta = 7;
    const target = new Date(Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + delta,
      slot.hour, 0, 0,
    ));
    // datetime-local expects local time without timezone suffix
    const pad = n => String(n).padStart(2, "0");
    const local = `${target.getFullYear()}-${pad(target.getMonth()+1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    onChange(local);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="schedule-modal">
        <h3 className="text-lg font-black tracking-tight mb-2">Schedule Campaign</h3>
        <p className="text-xs text-[#525252] mb-4">Enter the local date &amp; time the send should kick off.</p>
        <input type="datetime-local" value={value} onChange={e => onChange(e.target.value)}
               className="w-full h-10 px-3 border border-[#E5E5E5] mb-3" data-testid="schedule-datetime" />
        {insights?.top_slots?.length > 0 && insights.total_opens >= 30 && (
          <button onClick={applyBest}
                  className="w-full mb-4 p-2.5 text-left bg-[#EFF6FF] border border-[#0055FF] hover:bg-[#DBEAFE] transition-colors"
                  data-testid="best-time-chip">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0055FF]">Recommended send time</p>
            <p className="text-sm font-semibold text-[#0A0A0A]">
              {insights.top_slots[0].dow} · {insights.top_slots[0].hour}:00 UTC
              <span className="text-[#525252] font-normal"> — tap to use</span>
            </p>
          </button>
        )}
        {insights?.note && <p className="text-[11px] text-[#A3A3A3] mb-3">{insights.note}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button onClick={onConfirm} className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider" data-testid="schedule-confirm">Schedule</button>
        </div>
      </div>
    </div>
  );
}

/* ───────── Section: Details ───────── */
// F and I are declared at module scope so they keep a stable component
// identity across re-renders. Previously they were defined inside
// DetailsSection which meant every `update()` keystroke created a brand-new
// component type, forcing React to unmount+remount each input and losing
// focus after a single character — users reported "can't type in the fields".
const F = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</label>
    {children}
  </div>
);
const I = ({ val, onChange, placeholder, testid, refObj, fieldName, readOnly, onFocusField }) => (
  <input
    ref={el => { if (refObj) refObj.current = el; }}
    onFocus={() => fieldName && onFocusField && onFocusField({ field: fieldName, ref: refObj })}
    value={val || ""} onChange={e => onChange(e.target.value)}
    placeholder={placeholder} disabled={readOnly}
    className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF] disabled:bg-[#FAFAFA]"
    data-testid={testid} />
);

function DetailsSection({ camp, update, readOnly, campaignId }) {
  // Refs for each token-insertable input — so "click to insert" knows which field
  // is focused and where the caret is.
  const subjectRef = useRef(null);
  const previewRef = useRef(null);
  const [lastFocus, setLastFocus] = useState(null); // {field, ref}
  const [prediction, setPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);

  const runPrediction = async () => {
    if (!campaignId) return;
    setPredicting(true);
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/predict-performance`);
      setPrediction(r.data);
    } catch (e) {
      toast.error("Could not run prediction");
    }
    setPredicting(false);
  };

  const insertToken = (token) => {
    if (!lastFocus) return;
    const el = lastFocus.ref.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const val = el.value;
    const next = val.slice(0, start) + token + val.slice(end);
    update({ [lastFocus.field]: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
      <F label="Internal name"><I val={camp.name} onChange={v => update({ name: v })} placeholder="Q1 Newsletter" testid="input-name" readOnly={readOnly} /></F>
      <F label="Subscription type">
        <div className="text-xs text-[#525252] px-3 py-2 bg-[#FAFAFA] border border-[#E5E5E5]">Marketing Updates (Phase 1 default)</div>
      </F>
      <F label="Subject line">
        <I val={camp.subject} onChange={v => update({ subject: v })}
           placeholder="Hey {{first_name|default:there}}, …"
           testid="input-subject" refObj={subjectRef} fieldName="subject"
           readOnly={readOnly} onFocusField={setLastFocus} />
      </F>
      <F label="Preview text">
        <I val={camp.preview_text} onChange={v => update({ preview_text: v })}
           placeholder="Appears in the inbox after the subject"
           testid="input-preview" refObj={previewRef} fieldName="preview_text"
           readOnly={readOnly} onFocusField={setLastFocus} />
      </F>
      <F label="From name"><I val={camp.from_name} onChange={v => update({ from_name: v })} placeholder="Eyvo" testid="input-from-name" readOnly={readOnly} /></F>
      <F label="From email"><I val={camp.from_email} onChange={v => update({ from_email: v })} placeholder="hello@eyvo.com" testid="input-from-email" readOnly={readOnly} /></F>
      <F label="Reply-to"><I val={camp.reply_to} onChange={v => update({ reply_to: v })} placeholder="sales@eyvo.com" testid="input-reply-to" readOnly={readOnly} /></F>
      <div className="md:col-span-2">
        <F label="Internal note (not visible to recipients)">
          <textarea value={camp.internal_note || ""} onChange={e => update({ internal_note: e.target.value })}
                    disabled={readOnly} rows={2}
                    className="w-full px-3 py-2 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF] disabled:bg-[#FAFAFA]"
                    data-testid="input-note" />
        </F>
      </div>
      <div className="md:col-span-2 bg-[#FAFAFA] border border-[#E5E5E5] p-3 text-xs text-[#525252]">
        <strong className="text-[#0A0A0A]">Click to insert a variable into the focused field:</strong>
        <div className="mt-2 flex flex-wrap gap-1 font-mono text-[11px]">
          {["{{first_name|default:there}}", "{{last_name}}", "{{company_name}}", "{{email}}", "{{sender_name}}",
            "{{unsubscribe_url}}", "{{view_in_browser_url}}"].map(t =>
            <button key={t} type="button" onClick={() => insertToken(t)}
                    className={`bg-white border border-[#E5E5E5] px-1.5 py-0.5 transition-colors ${lastFocus ? "hover:border-[#0055FF] hover:text-[#0055FF] cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                    data-testid={`token-${t.replace(/[^a-z_]/gi, '')}`}>
              {t}
            </button>
          )}
        </div>
        {!lastFocus && <p className="text-[10px] text-[#A3A3A3] mt-1.5">Click the Subject or Preview text field first, then click a variable to insert it at the cursor.</p>}
      </div>

      {/* RSS feed panel — turns a campaign into an auto-populated feed digest */}
      <div className="md:col-span-2">
        <RssPanel campaignId={campaignId} readOnly={readOnly} />
      </div>

      {/* Predicted performance chip */}
      <div className="md:col-span-2" data-testid="predicted-performance">
        <PredictedPerformance
          prediction={prediction}
          predicting={predicting}
          runPrediction={runPrediction}
          onApplySuggestion={s => update({ subject: s })}
          hasSubject={!!camp.subject}
        />
      </div>
    </div>
  );
}

/* ───────── RSS feed panel (inside Details step) ───────── */
function RssPanel({ campaignId, readOnly }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!campaignId) { setLoading(false); return; }
    api.get(`/marketing/campaigns/${campaignId}/rss`)
      .then(r => {
        const d = r.data || {};
        setCfg({
          rss_feed_url: d.rss_feed_url || "",
          rss_schedule: d.rss_schedule || "daily",
          rss_max_items: d.rss_max_items || 5,
          rss_is_active: d.rss_is_active !== 0 && d.rss_is_active !== false,
          rss_item_template: d.rss_item_template || "",
          rss_last_checked_at: d.rss_last_checked_at,
        });
        if (d.rss_feed_url) setExpanded(true);
      })
      .catch(() => setCfg({ rss_feed_url: "", rss_schedule: "daily", rss_max_items: 5, rss_is_active: true, rss_item_template: "" }))
      .finally(() => setLoading(false));
  }, [campaignId]);

  if (loading || !cfg) return null;

  const save = async () => {
    if (!cfg.rss_feed_url.trim()) {
      toast.error("Enter an RSS feed URL first");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/marketing/campaigns/${campaignId}/rss`, {
        rss_feed_url: cfg.rss_feed_url,
        rss_schedule: cfg.rss_schedule,
        rss_max_items: parseInt(cfg.rss_max_items) || 5,
        rss_is_active: cfg.rss_is_active,
        rss_item_template: cfg.rss_item_template || null,
      });
      toast.success("RSS config saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const doPreview = async () => {
    if (!cfg.rss_feed_url.trim()) {
      toast.error("Enter a feed URL first");
      return;
    }
    await save(); // ensure server has latest url before rendering
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/rss/preview`);
      setPreview(r.data);
      toast.success(`Rendered ${r.data.item_count} item${r.data.item_count === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const set = (patch) => setCfg({ ...cfg, ...patch });

  return (
    <div className="bg-[#F0F7FF] border border-[#B6D5FF]" data-testid="rss-panel">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-[#E0EFFF] transition-colors"
        data-testid="rss-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0055FF]">RSS Feed Campaign</span>
          {cfg.rss_feed_url && (
            <span className="text-[10px] bg-white border border-[#0055FF] text-[#0055FF] px-1.5 py-0.5 font-bold">
              {cfg.rss_is_active ? "ACTIVE" : "PAUSED"}
            </span>
          )}
        </div>
        <span className="text-xs text-[#525252]">{expanded ? "Hide" : cfg.rss_feed_url ? "Edit" : "Configure"}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#B6D5FF]">
          <p className="text-[11px] text-[#525252]">
            Auto-populate this campaign's body from an RSS feed. The sweep updates <code>body_html</code> between
            <code>{" <!-- RSS:START --> "}</code> and <code>{" <!-- RSS:END --> "}</code> markers — your hero/footer stays intact.
          </p>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Feed URL</label>
            <input
              value={cfg.rss_feed_url}
              onChange={e => set({ rss_feed_url: e.target.value })}
              disabled={readOnly}
              placeholder="https://example.com/feed.xml"
              className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF] mt-1"
              data-testid="rss-url-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Schedule</label>
              <select
                value={cfg.rss_schedule}
                onChange={e => set({ rss_schedule: e.target.value })}
                disabled={readOnly}
                className="w-full h-9 px-2 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF] mt-1"
                data-testid="rss-schedule-select"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Max items</label>
              <input
                type="number"
                min="1"
                max="20"
                value={cfg.rss_max_items}
                onChange={e => set({ rss_max_items: e.target.value })}
                disabled={readOnly}
                className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF] mt-1"
                data-testid="rss-max-items-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Active</label>
              <label className="flex items-center gap-2 h-9 mt-1">
                <input
                  type="checkbox"
                  checked={cfg.rss_is_active}
                  onChange={e => set({ rss_is_active: e.target.checked })}
                  disabled={readOnly}
                  data-testid="rss-active-checkbox"
                />
                <span className="text-xs text-[#525252]">{cfg.rss_is_active ? "Sweeping" : "Paused"}</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
              Item template (optional HTML — <code>{"{{title}} {{link}} {{summary}} {{published}}"}</code>)
            </label>
            <textarea
              value={cfg.rss_item_template || ""}
              onChange={e => set({ rss_item_template: e.target.value })}
              disabled={readOnly}
              rows={3}
              placeholder="Leave blank for the default article block"
              className="w-full px-3 py-2 text-xs font-mono border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF] mt-1"
              data-testid="rss-template-input"
            />
          </div>
          {cfg.rss_last_checked_at && (
            <p className="text-[10px] text-[#A3A3A3]">Last checked: {formatLocal(cfg.rss_last_checked_at)}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || readOnly}
              className="h-8 px-3 bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
              data-testid="rss-save-btn"
            >{saving ? "Saving…" : "Save RSS config"}</button>
            <button
              onClick={doPreview}
              disabled={readOnly}
              className="h-8 px-3 bg-white border border-[#0055FF] text-[#0055FF] hover:bg-[#F0F7FF] text-xs font-bold uppercase tracking-wider disabled:opacity-50"
              data-testid="rss-preview-btn"
            >Preview</button>
          </div>
          {preview && (
            <div className="border border-[#E5E5E5] bg-white" data-testid="rss-preview-result">
              <div className="px-3 py-2 border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                {preview.item_count} item{preview.item_count === 1 ? "" : "s"} rendered
              </div>
              {/* RSS-rendered HTML preview. Backend campaigns.py already
                   sanitises body_html at write time and rss.py re-sanitises
                   the patched body; SafeHtml also runs DOMPurify at the DOM
                   boundary as defence in depth. */}
              <SafeHtml
                className="p-3 max-h-64 overflow-auto text-xs"
                data-testid="rss-preview-html"
                html={preview.rendered_html}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────── Section: Audience ───────── */

/* Picker that teaches the user the 4 ways to build an audience for a
 * campaign. Shipped 11-May-2026 after a user reported "it's not clear
 * how we create a list from clients or sales leads" — the three
 * underlying mechanisms (segment with source_type=client, segment with
 * source_type=crm_lead, CSV import) already existed, but the Campaign
 * Editor only showed "CSV Lists | Manual Lists | Segments" tabs with
 * no explanation of what each was for.
 *
 * Each card is a deep-link into the right creation flow — clicking
 * "Filter clients" opens the Segments page with the create-modal
 * pre-filled to kind=all_clients. The user can then narrow that
 * segment further if they want, and come back here to select it.
 */
function AudienceSourcePicker() {
  const options = [
    {
      key:   "clients",
      icon:  Buildings,
      title: "Filter Clients",
      blurb: "Pick all current clients, or narrow by country, status, signup date, etc.",
      cta:   "Build client segment",
      href:  "/marketing/segments?create=clients",
      color: "#16A34A",
    },
    {
      key:   "leads",
      icon:  UserCircle,
      title: "Filter Sales Leads",
      blurb: "Pick all CRM leads, or narrow by lead status, source, country, etc.",
      cta:   "Build lead segment",
      href:  "/marketing/segments?create=leads",
      color: "#7C3AED",
    },
    {
      key:   "import",
      icon:  Upload,
      title: "Import a List",
      blurb: "Upload a CSV from an external source (purchased list, event attendees, …).",
      cta:   "Upload CSV",
      href:  "/marketing/imports/new",
      color: "#0055FF",
    },
    {
      key:   "combine",
      icon:  StackIcon,
      title: "Combine Multiple",
      blurb: "Mix and match any of the above. Duplicates across sources are auto-removed.",
      cta:   "Use selector below ↓",
      href:  null,
      color: "#525252",
    },
  ];
  return (
    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 mb-4" data-testid="audience-source-picker">
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Build your audience</p>
        <p className="text-sm text-[#0A0A0A] mt-0.5">
          Three ways to define who gets this campaign. Pick any one — or combine.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {options.map(o => {
          const Icon = o.icon;
          const inner = (
            <>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 flex items-center justify-center" style={{ backgroundColor: `${o.color}15` }}>
                  <Icon size={16} weight="bold" style={{ color: o.color }} />
                </div>
                <div className="text-sm font-bold text-[#0A0A0A]">{o.title}</div>
              </div>
              <p className="text-[11px] text-[#525252] leading-relaxed mb-2">{o.blurb}</p>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: o.color }}>
                {o.cta}
              </div>
            </>
          );
          if (!o.href) {
            return (
              <div key={o.key}
                   className="bg-white border border-[#E5E5E5] p-3 flex flex-col"
                   data-testid={`audience-picker-${o.key}`}>
                {inner}
              </div>
            );
          }
          return (
            <a key={o.key} href={o.href} target="_blank" rel="noreferrer"
               className="bg-white border border-[#E5E5E5] p-3 flex flex-col hover:border-[#0055FF] hover:shadow-sm transition"
               data-testid={`audience-picker-${o.key}`}>
              {inner}
            </a>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-[#E5E5E5] text-[11px] text-[#A3A3A3] leading-relaxed">
        Once you've built a segment or imported a list, come back here and check it
        in the selector below. Multiple selections combine; suppressed contacts
        and cross-source duplicates are auto-removed before sending.
      </div>
    </div>
  );
}

function AudienceSection({ camp, lists, segments, toggleItem, isChecked, setIncludeClients, preview }) {
  const [tab, setTab] = useState("lists");
  const csvLists = lists.filter(l => l.list_type === "static_csv");
  const manualLists = lists.filter(l => l.list_type !== "static_csv");

  return (
    <>
      <AudienceSourcePicker />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
      <div className="bg-white border border-[#E5E5E5]">
        <div className="flex border-b border-[#E5E5E5]">
          {[
            { k: "lists", label: "CSV Lists", count: csvLists.length, color: "#0055FF" },
            { k: "manual", label: "Manual Lists", count: manualLists.length, color: "#525252" },
            { k: "segments", label: "Segments", count: segments.length, color: "#7C3AED" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
                      tab === t.k ? "bg-white text-[#0A0A0A] border-b-2 border-[#0055FF] -mb-[2px]" : "bg-[#FAFAFA] text-[#525252] hover:bg-white"
                    }`}
                    data-testid={`audience-tab-${t.k}`}>
              {t.label} ({t.count})
            </button>
          ))}
        </div>
        <div className="p-4 space-y-1 max-h-[500px] overflow-y-auto">
          {tab === "lists" && csvLists.map(l => (
            <AudienceRow key={l.id} checked={isChecked("list_ids", l.id)}
                         onToggle={() => toggleItem("list_ids", l.id)}
                         label={l.name} sub={`${l.member_count || 0} members · imported CSV`}
                         color="#0055FF" testid={`aud-list-${l.id}`} />
          ))}
          {tab === "manual" && manualLists.map(l => (
            <AudienceRow key={l.id} checked={isChecked("list_ids", l.id)}
                         onToggle={() => toggleItem("list_ids", l.id)}
                         label={l.name} sub={`${l.member_count || 0} members`}
                         color="#525252" testid={`aud-list-${l.id}`} />
          ))}
          {tab === "segments" && segments.map(s => (
            <AudienceRow key={s.id} checked={isChecked("segment_ids", s.id)}
                         onToggle={() => toggleItem("segment_ids", s.id)}
                         label={s.name} sub={`${s.segment_kind.replace(/_/g, " ")}`}
                         color="#7C3AED" testid={`aud-seg-${s.id}`} />
          ))}
          {((tab === "lists" && csvLists.length === 0) ||
            (tab === "manual" && manualLists.length === 0) ||
            (tab === "segments" && segments.length === 0)) && (
            <p className="text-xs text-[#A3A3A3] p-4">No items in this tab yet.</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Recipient count</p>
        <p className="text-4xl font-black tabular-nums text-[#0A0A0A] mt-1 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="audience-count">
          {preview?.total ?? "—"}
        </p>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">By source</p>
        <div className="space-y-2" data-testid="audience-breakdown">
          {["csv_import", "client", "crm_lead"].map(k => {
            const n = preview?.by_source?.[k] || 0;
            const total = preview?.total || 1;
            const pct = Math.round((n / total) * 100);
            const c = SOURCE_COLORS[k];
            return (
              <div key={k} className="flex items-center gap-2 text-xs">
                <SourceChip source={k} />
                <div className="flex-1 h-1.5 bg-[#F5F5F5] overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: c.fg }} />
                </div>
                <span className="tabular-nums text-[#525252] w-10 text-right">{n}</span>
              </div>
            );
          })}
        </div>
        {preview?.error && (
          <p className="mt-3 text-xs text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] p-2" role="alert" data-testid="audience-error">
            {preview.error}
          </p>
        )}
        <div className="mt-4 pt-4 border-t border-[#F5F5F5] text-xs" data-testid="audience-clients">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-[#0055FF]"
                   checked={camp?.audience_definition?.include_clients === true}
                   onChange={(e) => setIncludeClients(e.target.checked)}
                   data-testid="audience-include-clients" />
            <span>
              <span className="font-bold text-[#0A0A0A]">This is a client mailing</span>
              <span className="block text-[11px] text-[#737373]">
                Client contacts (on a client record, a client CC list, or imported from a client) are left out of
                campaigns unless this is ticked or you target an &ldquo;All clients&rdquo; segment.
              </span>
            </span>
          </label>
          {preview && !preview.error && (
            <p className="mt-2 text-[11px] text-[#525252]" data-testid="audience-clients-summary">
              {preview.include_clients
                ? "Client contacts are included in this campaign."
                : `${(preview.clients_excluded || 0).toLocaleString()} client contact${preview.clients_excluded === 1 ? "" : "s"} left out.`}
            </p>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-[#F5F5F5] text-[11px] text-[#A3A3A3]">
          Suppressed contacts and duplicates across sources are automatically removed.
        </div>
      </div>
      </div>
    </>
  );
}

function AudienceRow({ checked, onToggle, label, sub, color, testid }) {
  return (
    <label className="flex items-center gap-3 p-2 hover:bg-[#FAFAFA] cursor-pointer border border-transparent hover:border-[#E5E5E5]" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="w-4 h-4" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0A0A0A] truncate">{label}</p>
        <p className="text-[11px] text-[#A3A3A3] truncate">{sub}</p>
      </div>
      <div className="w-1 h-8" style={{ backgroundColor: color }} />
    </label>
  );
}

/* ───────── Section: Content ───────── */
function ContentSection({ camp, update, readOnly, previewDevice, setPreviewDevice,
                         previewContactId, setPreviewContactId, previewContacts,
                         previewRendered, loadPreview }) {
  // Try to recover blocks from the current body_html. If none, start in HTML mode.
  const initialBlocks = extractBlocks(camp.body_html);
  const [mode, setMode] = useState(initialBlocks ? "blocks" : "html");
  const [blocks, setBlocks] = useState(initialBlocks || emptyBlocks());

  const syncBlocksToHtml = (next) => {
    setBlocks(next);
    const visible = renderBlocksToHtml(next);
    update({ body_html: wrapBlocksInHtml(next, visible) });
  };

  const switchToBlocks = () => {
    // Reuse existing blocks if already present in body_html; otherwise wrap
    // the raw HTML in a single custom_html block so the Blocks view and the
    // HTML view reflect exactly the same content.
    const found = extractBlocks(camp.body_html);
    const seed = found || blocksFromRawHtml(camp.body_html);
    setBlocks(seed);
    const visible = renderBlocksToHtml(seed);
    update({ body_html: wrapBlocksInHtml(seed, visible) });
    setMode("blocks");
  };

  const switchToHtml = () => {
    // Dropping to raw HTML drops the block marker. The user now owns the HTML.
    const visible = renderBlocksToHtml(blocks);
    update({ body_html: visible });
    setMode("html");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 bg-[#F5F5F5] p-0.5 w-fit" data-testid="content-mode-switch">
        <button
          onClick={switchToBlocks}
          disabled={readOnly}
          className={`px-3 h-7 text-[11px] font-bold uppercase tracking-wider ${mode === "blocks" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#525252]"}`}
          data-testid="mode-blocks"
        >
          Designer
        </button>
        <button
          onClick={switchToHtml}
          disabled={readOnly}
          className={`px-3 h-7 text-[11px] font-bold uppercase tracking-wider ${mode === "html" ? "bg-white shadow-sm text-[#0055FF]" : "text-[#525252]"}`}
          data-testid="mode-html"
        >
          HTML
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-4">
        <div className="bg-white border border-[#E5E5E5] p-4 overflow-x-auto">
          {mode === "blocks" ? (
            <div className="pl-6">
              <EmailBlockBuilder blocks={blocks} onBlocksChange={readOnly ? () => {} : syncBlocksToHtml} />
              <p className="text-[11px] text-[#A3A3A3] mt-3">Drag blocks to reorder. Variables like <code>{"{{first_name|default:there}}"}</code> are expanded at send time.</p>
            </div>
          ) : (
            <>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] block mb-1">HTML body</label>
              <textarea value={camp.body_html || ""} onChange={e => update({ body_html: e.target.value })}
                        disabled={readOnly}
                        placeholder={"<h1>Hi {{first_name|default:there}}!</h1>\n<p>…</p>"}
                        className="w-full h-[520px] px-3 py-2 text-xs font-mono border border-[#E5E5E5] bg-[#FAFAFA] focus:outline-none focus:border-[#0055FF] disabled:opacity-60"
                        data-testid="body-html" />
              <p className="text-[11px] text-[#A3A3A3] mt-1">Use the variables listed on the Details step. Plain-text is auto-generated.</p>
            </>
          )}
        </div>

        <div className="bg-white border border-[#E5E5E5]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
            <div className="flex items-center gap-1">
              <button onClick={() => setPreviewDevice("desktop")}
                      className={`p-1.5 ${previewDevice === "desktop" ? "bg-[#0055FF] text-white" : "text-[#525252]"}`}
                      data-testid="preview-desktop"><Desktop size={14} /></button>
              <button onClick={() => setPreviewDevice("mobile")}
                      className={`p-1.5 ${previewDevice === "mobile" ? "bg-[#0055FF] text-white" : "text-[#525252]"}`}
                      data-testid="preview-mobile"><DeviceMobile size={14} /></button>
            </div>
            <select value={previewContactId} onChange={e => setPreviewContactId(e.target.value)}
                    className="text-xs px-2 py-1 border border-[#E5E5E5]"
                    data-testid="preview-as">
              <option value="">Preview as synthetic contact</option>
              {previewContacts.slice(0, 20).map(c =>
                <option key={c.id} value={c.id}>{c.full_name || c.email}</option>
              )}
            </select>
            <button onClick={loadPreview} className="text-xs text-[#0055FF] font-bold uppercase tracking-wider" data-testid="preview-refresh">
              Refresh preview
            </button>
          </div>
          <div className="bg-[#FAFAFA] p-4 flex justify-center min-h-[500px]">
            <div className={`bg-white shadow-sm transition-all ${previewDevice === "mobile" ? "max-w-[360px]" : "max-w-[640px]"} w-full`}>
              <div className="px-4 py-2 border-b border-[#E5E5E5] text-[11px] text-[#A3A3A3] bg-[#FAFAFA]">
                <strong className="text-[#0A0A0A]">Subject:</strong> {previewRendered?.subject || <em>(set a subject)</em>}
                {previewRendered?.preview_text && <><br /><span className="opacity-70">{previewRendered.preview_text}</span></>}
              </div>
              <iframe
                title="preview"
                /* XSS: srcDoc is sanitised with DOMPurify so even if a legacy
                   campaign row predates the backend hardening, scripts and
                   event handlers are stripped before the iframe parses the
                   document. Combined with sandbox="" (no allow-scripts, no
                   allow-same-origin), this is defence in depth. */
                srcDoc={safeSrcDoc(previewRendered?.body_html) || "<p style='color:#A3A3A3;padding:24px;font-family:Arial'>Nothing to preview yet.</p>"}
                sandbox=""
                referrerPolicy="no-referrer"
                className="w-full"
                style={{ height: 480, border: 0 }}
                data-testid="preview-iframe"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── Section: Review & Send ───────── */
function ReviewSection({ camp, audiencePreview, previewRendered, lint, testEmails, setTestEmails,
                        onTestSend, onSendNow, onSchedule, onRequestApproval, update }) {
  const missing = [];
  if (!camp.subject) missing.push("subject");
  if (!camp.from_email) missing.push("from email");
  if (!camp.body_html) missing.push("HTML body");
  if (!audiencePreview?.total) missing.push("audience with recipients");
  const approvalStatus = camp.approval_status || "draft";
  const isApproved = approvalStatus === "approved";
  const scoreColor = lint ? (lint.score >= 85 ? "#16A34A" : lint.score >= 70 ? "#0055FF" : lint.score >= 50 ? "#D97706" : "#DC2626") : "#A3A3A3";

  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const fixIssues = async () => {
    setFixing(true);
    try {
      const r = await api.post("/marketing/pulse-ai/fix-flagged-issues", {
        subject: camp.subject || "",
        body_html: camp.body_html || "",
        issues: (lint?.issues || []).map(it => ({ severity: it.severity, msg: it.message || it.msg })),
      });
      setFixResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pulse AI failed");
    }
    setFixing(false);
  };
  const applyFix = () => {
    if (!fixResult) return;
    update({ subject: fixResult.subject, body_html: fixResult.body_html });
    toast.success("Applied Pulse AI rewrites");
    setFixResult(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
      <div className="bg-white border border-[#E5E5E5] p-5 space-y-4">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Final review</h2>

        <ReviewRow label="Subject" value={previewRendered?.subject || camp.subject} mono />
        <ReviewRow label="Preview text" value={previewRendered?.preview_text || camp.preview_text || <span className="text-[#A3A3A3]">—</span>} />
        <ReviewRow label="From" value={`${camp.from_name || "Eyvo"} <${camp.from_email || "—"}>`} />
        <ReviewRow label="Reply-to" value={camp.reply_to || "—"} />
        <ReviewRow label="Recipients" value={
          <span><strong>{audiencePreview?.total ?? "—"}</strong>
            {audiencePreview?.by_source && (
              <span className="ml-2 text-xs text-[#A3A3A3]">
                ({audiencePreview.by_source.csv_import} CSV · {audiencePreview.by_source.client} clients · {audiencePreview.by_source.crm_lead} leads)
              </span>
            )}
            {audiencePreview && !audiencePreview.error && (
              <span className="ml-2 text-xs text-[#525252]" data-testid="review-clients">
                {audiencePreview.include_clients ? "· client mailing" : `· ${audiencePreview.clients_excluded || 0} client contacts left out`}
              </span>
            )}
            {audiencePreview?.error && <span className="ml-2 text-xs text-[#991B1B]">{audiencePreview.error}</span>}
          </span>
        } />

        {/* Deliverability score */}
        {lint && (
          <div className="border border-[#E5E5E5] p-3" data-testid="lint-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="text-3xl font-black tabular-nums" style={{ color: scoreColor, fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {lint.score}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: scoreColor }}>{lint.label}</p>
                <p className="text-[10px] text-[#A3A3A3]">Deliverability score</p>
              </div>
            </div>
            {lint.issues.length > 0 && (
              <ul className="text-xs space-y-1 mt-2" data-testid="lint-issues">
                {lint.issues.map((iss, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className={`text-[9px] font-bold uppercase px-1 py-[1px] ${iss.severity === "high" ? "bg-[#FEF2F2] text-[#DC2626]" : iss.severity === "medium" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F5F5F5] text-[#525252]"}`}>
                      {iss.severity}
                    </span>
                    <span className="text-[#525252]">{iss.message}</span>
                  </li>
                ))}
              </ul>
            )}
            {lint.issues.length > 0 && lint.score < 85 && !fixResult && (
              <button onClick={fixIssues} disabled={fixing}
                      className="mt-3 h-8 px-3 bg-[#0A0A0A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#0055FF] disabled:opacity-40 flex items-center gap-1"
                      data-testid="fix-issues-btn">
                {fixing ? "Pulse AI thinking…" : "Fix issues with Pulse AI"}
              </button>
            )}
            {fixResult && (
              <div className="mt-3 border border-[#0055FF] bg-[#EFF6FF] p-3 space-y-2" data-testid="fix-result-card">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0055FF]">Pulse AI rewrite</p>
                <div className="text-xs">
                  <p className="font-bold text-[#0A0A0A]">New subject</p>
                  <p className="font-mono bg-white border border-[#CBD5E1] px-2 py-1 mt-1">{fixResult.subject}</p>
                </div>
                {Array.isArray(fixResult.changelog) && fixResult.changelog.length > 0 && (
                  <ul className="text-[11px] text-[#525252] list-disc pl-4 space-y-0.5">
                    {fixResult.changelog.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                )}
                <div className="flex gap-2 pt-1">
                  <button onClick={applyFix}
                          className="h-7 px-3 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#15803D]"
                          data-testid="fix-apply-btn">
                    Apply rewrite
                  </button>
                  <button onClick={() => setFixResult(null)}
                          className="h-7 px-3 border border-[#E5E5E5] text-[11px] font-bold uppercase tracking-wider text-[#525252] hover:border-[#0055FF]"
                          data-testid="fix-discard-btn">
                    Discard
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {missing.length > 0 && (
          <div className="bg-[#FEF2F2] border border-[#DC2626] p-3 text-xs text-[#DC2626]" data-testid="missing-fields">
            <strong className="uppercase tracking-wider">Cannot send yet</strong> — missing: {missing.join(", ")}.
          </div>
        )}

        <div className="pt-2 border-t border-[#F5F5F5]">
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Send a test first</label>
          <div className="flex gap-2 mt-1">
            <input value={testEmails} onChange={e => setTestEmails(e.target.value)}
                   placeholder="comma-separated internal emails"
                   className="flex-1 h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                   data-testid="test-emails" />
            <button onClick={onTestSend}
                    className="h-9 px-3 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                    data-testid="test-send-btn">
              <Envelope size={12} />Test send
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] p-5 space-y-3 h-fit">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Ready to send</h2>

        {/* Approval gate status */}
        {!isApproved && (
          <div className="border border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid="review-approval-gate">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#92400E] flex items-center gap-1">
              <ShieldCheck size={12} />
              Approval required
            </p>
            <p className="text-xs text-[#525252] mt-1">
              {approvalStatus === "pending_approval"
                ? "Awaiting a platform admin's approval. You cannot send or schedule yet."
                : "A platform admin must approve this campaign before it can be sent or scheduled."}
            </p>
            {approvalStatus !== "pending_approval" && (
              <button onClick={onRequestApproval} disabled={missing.length > 0}
                      className="mt-2 h-8 px-3 bg-[#0A0A0A] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40"
                      data-testid="request-approval-btn-review">
                Request approval
              </button>
            )}
          </div>
        )}

        <button onClick={onSendNow} disabled={missing.length > 0 || !isApproved}
                className="w-full h-11 bg-[#0055FF] hover:bg-[#0040CC] text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="send-now-btn">
          <PaperPlaneRight size={16} weight="fill" />
          Send Now
        </button>
        <button onClick={onSchedule} disabled={missing.length > 0 || !isApproved}
                className="w-full h-11 border border-[#E5E5E5] hover:border-[#0055FF] text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="schedule-btn">
          <CalendarBlank size={14} />
          Schedule for later
        </button>
        <p className="text-[11px] text-[#A3A3A3] pt-2 border-t border-[#F5F5F5]">
          Sends go out via SendGrid in batches of 100. Campaign state tracks progress —
          you can pause mid-send from the report page.
        </p>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, mono }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] w-24 pt-0.5 shrink-0">{label}</div>
      <div className={`flex-1 text-[#0A0A0A] ${mono ? "font-mono text-xs" : ""}`}>{value || <span className="text-[#A3A3A3] italic">not set</span>}</div>
    </div>
  );
}

/* ───────── Predicted Performance chip ───────── */
function PredictedPerformance({ prediction, predicting, runPrediction, onApplySuggestion, hasSubject }) {
  if (!prediction) {
    return (
      <div className="border border-[#E5E5E5] bg-white p-4 flex items-center justify-between gap-3" data-testid="predict-chip-initial">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Pulse AI · Predicted performance</p>
          <p className="text-sm text-[#0A0A0A] mt-0.5">
            Get a forecasted open-rate and 3 subject-line rewrites based on your recent campaign history.
          </p>
        </div>
        <button
          onClick={runPrediction}
          disabled={predicting || !hasSubject}
          className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#0055FF] disabled:opacity-40 shrink-0"
          data-testid="predict-run"
        >
          {predicting ? "Analyzing…" : hasSubject ? "Predict performance" : "Add a subject first"}
        </button>
      </div>
    );
  }

  const baseline = prediction.baseline || {};
  const heur = prediction.heuristic || {};
  const llm = prediction.llm || {};
  const forecast = heur.forecast_open_rate;
  const baseOpen = baseline.open_rate;
  const confidence = heur.confidence || "low";
  const confColor = { high: "#16A34A", medium: "#D97706", low: "#DC2626", unknown: "#A3A3A3" }[confidence];

  const pct = (v) => v == null ? "—" : `${(v * 100).toFixed(1)}%`;

  return (
    <div className="border border-[#0055FF] bg-[#EFF6FF] p-4 space-y-3" data-testid="predict-chip-result">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0055FF]">Predicted open rate</p>
            <p className="text-2xl font-black tabular-nums text-[#0A0A0A]" data-testid="predict-forecast">
              {pct(forecast)}
            </p>
            <p className="text-[11px] text-[#525252]">
              Baseline: <span className="tabular-nums">{pct(baseOpen)}</span>
              {baseline.sample_campaigns > 0 && <> · {baseline.sample_campaigns} past campaign{baseline.sample_campaigns === 1 ? "" : "s"}</>}
            </p>
          </div>
          <div className="h-10 w-px bg-[#CBD5E1]" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#525252]">Confidence</p>
            <p className="text-sm font-bold tabular-nums" style={{ color: confColor }}>
              {confidence.toUpperCase()}
            </p>
          </div>
        </div>
        <button onClick={runPrediction} disabled={predicting}
                className="text-[10px] font-bold text-[#0055FF] hover:underline uppercase tracking-wider"
                data-testid="predict-refresh">
          {predicting ? "…" : "Refresh"}
        </button>
      </div>

      {heur.adjustments && heur.adjustments.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {heur.adjustments.map((a, i) => {
            const up = a.delta_pct_points > 0;
            return (
              <span key={i} className={`px-2 py-0.5 text-[10px] font-mono border ${up ? "border-[#16A34A] text-[#15803D] bg-[#F0FDF4]" : "border-[#DC2626] text-[#991B1B] bg-[#FEF2F2]"}`}>
                {a.factor}: {up ? "+" : ""}{a.delta_pct_points} pp
              </span>
            );
          })}
        </div>
      )}

      {llm.critique && (
        <p className="text-xs text-[#0A0A0A] bg-white px-3 py-2 border border-[#CBD5E1]">
          <strong className="text-[#0055FF]">Copy critique:</strong> {llm.critique}
        </p>
      )}

      {Array.isArray(llm.suggestions) && llm.suggestions.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">Subject-line rewrites</p>
          <div className="space-y-1.5">
            {llm.suggestions.map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-[#CBD5E1] px-3 py-2">
                <div className="flex-1">
                  <p className="text-sm text-[#0A0A0A] font-semibold">{s.subject}</p>
                  {s.why && <p className="text-[11px] text-[#525252] mt-0.5">{s.why}</p>}
                </div>
                <button
                  onClick={() => onApplySuggestion(s.subject)}
                  className="h-7 px-2 bg-[#0055FF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#003ECB] shrink-0"
                  data-testid={`predict-apply-${i}`}
                >
                  Use this
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



/* ───────── Approval bar ───────── */
const APPROVAL_STATES = {
  draft: { bg: "#F5F5F5", fg: "#525252", label: "Draft — not submitted", Icon: Clock },
  pending_approval: { bg: "#FFFBEB", fg: "#92400E", label: "Pending admin approval", Icon: Clock },
  approved: { bg: "#F0FDF4", fg: "#15803D", label: "Approved", Icon: ShieldCheck },
};

function ApprovalBar({ camp, isAdmin, onRequest, onApprove, onReject }) {
  const st = camp.approval_status || "draft";
  const meta = APPROVAL_STATES[st] || APPROVAL_STATES.draft;
  const Icon = meta.Icon;
  // A sent campaign is editable. The send already happened and
  // `mkt_sends` holds the personalisation snapshot each recipient
  // actually received, so editing the campaign record cannot rewrite
  // history — it lets an operator correct copy and re-use it.
  // `sending` stays locked: a send in flight would give the first
  // half of the audience one email and the second half another.
  const readOnly = ["sending", "archived"].includes(camp.status);
  return (
    <div
      className="border p-3 mb-4 flex items-center gap-3 flex-wrap text-xs"
      style={{ backgroundColor: meta.bg, borderColor: meta.fg }}
      data-testid="approval-bar"
    >
      <Icon size={14} style={{ color: meta.fg }} />
      <span className="font-bold uppercase tracking-wider" style={{ color: meta.fg }}>
        {meta.label}
      </span>
      {st === "approved" && camp.approved_at && (
        <span className="text-[11px] text-[#525252]">
          · approved {formatLocalDate(camp.approved_at)}
        </span>
      )}
      {camp.approval_notes && (
        <span className="text-[11px] text-[#525252] italic truncate max-w-[40ch]">
          Note: {camp.approval_notes}
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {!readOnly && st === "draft" && (
          <button onClick={onRequest}
                  className="h-8 px-3 bg-[#0A0A0A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#0055FF]"
                  data-testid="request-approval-btn">
            Request approval
          </button>
        )}
        {!readOnly && isAdmin && st === "pending_approval" && (
          <>
            <button onClick={onApprove}
                    className="h-8 px-3 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#15803D] flex items-center gap-1"
                    data-testid="approve-btn">
              <Check size={11} weight="bold" />Approve
            </button>
            <button onClick={onReject}
                    className="h-8 px-3 border border-[#DC2626] text-[#DC2626] text-[11px] font-bold uppercase tracking-wider hover:bg-[#FEF2F2] flex items-center gap-1"
                    data-testid="reject-btn">
              <X size={11} weight="bold" />Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}
